import { createMarketplaceClient } from '@lib/api/usg/marketplace';
import { EnvironmentVariable, getEnvVar } from '@lib/config';
import { failingFetcher } from '@lib/http/fetch';
import { getWithoutLocale } from 'utils';

export enum CompassUIPageRoutes {
  SEARCH = '/search',
  LISTING = '/listing',
  USERS = '/users',
  LANDING = '/landing/',
  HOMEPAGE = '/homepage',
  PACKAGES = '/packages',
  PRODUCT = '/product',
  PUBLISHER = '/publisher',
  ACCOUNT_VOUCHER = '/account/voucher',
  ACCESS_DENIED_JV_CHINA = '/restricted-region',
}

/**
 * Used to determine if a page is a Compass-UI page, or a legacy asv2 page.
 * When new Compass UI pages are added, they need to be added to the CompassUIPageRoutes enum.
 */
export const isCompassUIPage = async (pathname: string): Promise<boolean> => {
  const pathnameNoLocale = getWithoutLocale(pathname) || '/';
  const isBuiltInRoute =
    pathnameNoLocale === '/' ||
    Object.values(CompassUIPageRoutes).some((route) =>
      pathnameNoLocale.startsWith(route)
    );

  if (isBuiltInRoute) {
    return true;
  }

  const { slugs } = await failingFetcher(
    `${getEnvVar(EnvironmentVariable.siteUrl)}/api/cms/landing-page-slugs`,
    {}
  );
  return Boolean(slugs && slugs[pathnameNoLocale]);
};

export const resolveProductRedirect = async (
  pathname: string
): Promise<{ redirect: boolean; route: string }> => {
  const pathSegments = pathname.split('/').filter(Boolean);
  const isProductIdPath =
    pathSegments[0] === 'packages' &&
    pathSegments[1] === 'package' &&
    !Number.isNaN(Number(pathSegments[2]));

  if (!isProductIdPath) {
    return { redirect: false, route: '' };
  }

  const productId = pathSegments[2];
  const marketplaceClient = createMarketplaceClient();

  try {
    const { category, slug, id, canonicalId } =
      await marketplaceClient.getProduct({
        productId,
      });

    const targetId = canonicalId ?? id;
    const route = category
      ? `/packages/${category.slug}/${slug}-${targetId}`
      : `/packages/${slug}-${targetId}`;
    return {
      redirect: true,
      route,
    };
  } catch (error) {
    console.error(`Error fetching product with id ${productId}:`, error);
    return { redirect: false, route: '' };
  }
};
